import { queryOptions } from '@tanstack/react-query';

import {
  apiGet,
  apiPatch,
  apiPost,
  type ApiError,
  type BodyOf,
  type QueryOf,
  type ResponseOf,
} from './http';
import type { QuestionSet } from './questionSets';
import { queryKeys } from './queryKeys';

/**
 * The vacancy catalogue (§6.8) — the six role routes, in one module, over the one `apiClient`.
 *
 * ⚠️ **The question routes left this module with Sprint 19** (ADR 0018): `/api/v1/roles/{role_id}/
 * questions/…` is **gone from the document**, every one of them. ⚠️ **This sentence went on to say
 * *"a question is now a tenant-wide library entry with its own module (`api/questions.ts`)"* until
 * 2026-09-24, and that wording is retracted with the module** (ADR 0019): a question belongs to
 * exactly one set and its helpers are `api/questionSets.ts`'s. What survives here is `RoleOut.
 * active_question_count`, with the same meaning and the same number — it counts the active questions
 * of the sets attached to the vacancy.
 *
 * Everything goes through `http.ts`'s `apiGet`/`apiPost`/`apiPatch`, and every request and response
 * type is derived from `schema.d.ts`. Two things are worth knowing before adding to it:
 *
 *  * **There is no `DELETE` on any route here, and there is no helper for one.** A vacancy is
 *    *closed* (`updateRole` with `{ is_open: false }` — `RoleUpdate`'s own docstring: *"that is what
 *    'delete' means for a vacancy, which is not deletable through any surface"*). The document holds
 *    the rule at the schema level, and `backend/tests/.../test_api.py::
 *    test_the_openapi_schema_has_no_delete_operation_under_roles` is what keeps it there.
 *  * **No body here carries a tenant or a `status`.** The tenant is the session
 *    (`.claude/rules/tenancy.md`); every body type is `BodyOf<…>`, which is how a field like that
 *    stops being possible to add by accident.
 *
 * Nothing here catches an `ApiError`. A 401 signs the app out (`queryClient.ts`) and a 429 reads as a
 * throttle — both decided once, in `http.ts` and `ApiErrorState`.
 *
 * ⚠️ **A 404 from a route here means *"no such vacancy"*, never *"not yours"* — and that is a
 * decision, not an omission.** `CANDIDATE_SCOPED_RECORD` in `http.ts` maps a 404 on the *candidate*
 * surface to §8's Forbidden state, because that surface is narrowed per caller (backend TASK-029) and
 * a 403 there would confirm that a candidate exists. These routes are **refused** per caller instead
 * (an Interviewer is 403 on the catalogue), so nothing about a 404 here is a reach answer and
 * `isOutsideReach` must stay `false` for it. Nothing is added to that pattern.
 */

/** `?page=`, `?page_size=` — the catalogue's whole query, read off the schema. */
export type RoleListQuery = NonNullable<QueryOf<'/api/v1/roles/', 'get'>>;

/** `PagedRoleOut` — the page and the tenant-wide `count` a screen needs to say *"1–20 of 34"*. */
export type RolePage = ResponseOf<'/api/v1/roles/', 'get'>;

/**
 * `RoleOut` — a vacancy as both the catalogue and the retrieve answer with.
 *
 * ⚠️ **Both counts are *active* counts and neither is a total**, which the payload's own docstring
 * says: `active_question_count` excludes archived questions and `active_candidate_count` counts only
 * the `NEW` / `INTERVIEWING` / `OFFER` runs. There is **no** total question count on this schema, so
 * §6.8's grid can say *"7 active questions"* and cannot say *"7 of 9"*, and inventing a field to give
 * it a denominator is the one thing that would make it disagree with the server.
 *
 * ⚠️ **`active_question_count` survived ADR 0018 with the same meaning and the same number**, and what
 * changed is only where it is counted from: the active questions of the **sets attached to the
 * vacancy** rather than of a bank the vacancy owned. This docblock's second half said the bank screen
 * could render *"7 of 9"* for this vacancy — corrected 2026-08-11, because there is no per-vacancy
 * question screen any more. ⚠️ **The reason given then was *"§6.9 is the tenant's library and knows
 * nothing about a vacancy"*, and that wording is retracted** (2026-09-24, ADR 0019): §6.9 is the
 * tenant's **question sets**, it still knows nothing about a vacancy, and the conclusion is the one
 * that survives both moves.
 */
export type Role = RolePage['items'][number];

/** `RoleIn` — title, department, abbreviation and `is_open`; no tenant, ever. */
export type NewRole = BodyOf<'/api/v1/roles/', 'post'>;

/**
 * `RoleUpdate` — every field optional, and **omittable rather than nullable**: an explicit
 * `{"title": null}` is a 422, not a `NULL` written onto a NOT NULL column. Whatever builds this body
 * drops the fields it did not touch instead of sending `null` for them (asserted on TASK-072's form).
 */
export type RoleEdit = BodyOf<'/api/v1/roles/{role_id}/', 'patch'>;

/**
 * The catalogue, page by page.
 *
 * ⚠️ **Admin and Recruiter only** — `Route("get", ROLES, None, (401, 403, 200, 200, 403, 403))` in
 * `backend/tests/test_permission_matrix.py` answers an **Interviewer 403**, which is the route's own
 * Sprint 2 group gate (`read_access = require_group(GROUP_ADMIN, GROUP_RECRUITER)` in
 * `backend/src/apps/vacancies/api.py`, `b1e125d`). It is **not** TASK-029, Sprint 4's rule that the
 * *candidate* surface refuses an Interviewer with 404 rather than 403 — a mis-citation this docblock
 * carried once and Sprint 9's re-review caught. §6.3's vacancy filter therefore does not issue this
 * request for an Interviewer at all and builds its options from their own rows (`vacancyOptions`).
 */
export const fetchRoles = (query: RoleListQuery = {}) => apiGet('/api/v1/roles/', { query });

/**
 * ⚠️ **The key carries the query, and until TASK-070 it did not** — `queryKeys.roles()` was the
 * constant `['roles']`, so the first caller to ask for `?page=2` would have overwritten the entry
 * §6.3's filter and §6.4's dialog read and served them page 2 as the catalogue, with every existing
 * test green. The full argument, viewer included, is in `queryKeys.roles`'s docblock; what belongs
 * here is that the query reaching the key and the query reaching the URL are the **same object**, so
 * they cannot drift.
 *
 * **No `placeholderData` and no `select` on this or any factory below.** Sprint 8 shipped
 * `keepPreviousData` on a correctly keyed query and it resolved from the observer's last payload
 * under *any* key while reporting `status: 'success'` — a paginated grid is exactly where that looks
 * like a feature.
 *
 * The viewer is deliberately absent: this route is *refused* per caller, never *narrowed*. The
 * residual hazard — an entry outliving the permission that filled it — is closed at the **read**
 * (`VacancyFilter` passes the catalogue on only when `mayReadVacancyCatalogue`), which is where a
 * viewer segment could not have closed it, because a demotion without a re-login keeps the user id.
 */
export function rolesQuery(query?: RoleListQuery) {
  return queryOptions<RolePage, ApiError>({
    queryKey: queryKeys.roles(query),
    queryFn: () => fetchRoles(query),
  });
}

/*
 * ⚠️ **`roleQuery(roleId)` was here and is deleted** (2026-08-11, TASK-135). It read one vacancy by id
 * and its **only** caller was §6.9, which named itself after the vacancy its bank belonged to; ADR 0018
 * took the vacancy out of that screen's address. A `queryOptions` factory with no caller is a request
 * nobody wants and is the `applyTenantAccent` mistake Sprint 8 paid for twice, so it goes rather than
 * waiting for a screen. The 404 decision it carried is unchanged and is this module's header's:
 * a 404 from a vacancy route means *"no such vacancy"*, never *"not yours"*.
 *
 * ⚠️ **`queryKeys.role` was kept in that pass and is deleted now** (2026-09-28, BUG-079). This note
 * read *"deliberately kept … it is in the report for `BUGS.md`"*, and the report answered: a key
 * builder with no query behind it does not earn its place. The question it was held open for —
 * whether `api.test.ts`'s family-shape assertion or §6.8's two dialogs needed it — has the same
 * answer on both counts, because neither ever called it: the assertion demonstrated a prefix rule
 * that `questionSet` demonstrates as well, and the dialogs invalidate by **root**, which reaches
 * every entry in the family whether or not a builder exists for one of them.
 */

/**
 * **Admin only** — `(401, 403, 403, 201, 403, 403)`: a Recruiter may read the catalogue, not add to it.
 *
 * `is_open` is **stated**, not omitted. The document defaults it to `true`, so the route accepts a
 * body without it while the generated type requires it — the same mismatch `NewPersonAttempt`
 * documents, and the opposite decision, for a reason: there the field is ADR 0014's override and a
 * body carrying it even as `false` is one character from defeating duplicate detection, whereas here
 * it is an ordinary field whose value the form already knows. So there is no `Omit<…>` narrowing and
 * no assertion here.
 */
export const createRole = (role: NewRole) => apiPost('/api/v1/roles/', role);

/**
 * **Admin only** — `(401, 403, 403, 200, 403, 403)`. This is also how a vacancy is *closed*:
 * `{ is_open: false }` is what "delete" means here, and there is no delete to write instead.
 */
export const updateRole = (roleId: number, changes: RoleEdit) =>
  apiPatch('/api/v1/roles/{role_id}/', changes, { params: { role_id: roleId } });

/**
 * `RoleOptionOut` — one choice in §6.3's vacancy filter: **four** keys, and nothing else.
 *
 * ⚠️ **It was `{id, title}` until 2026-11-03** (backend TASK-184, ADR 0020 decision 9). A title is
 * not an identifier: the first real ADP import wrote 95 requisitions of which several share a
 * title, so the filter offered *Firmware Engineer* seven times. `requisition_number` and
 * `hiring_manager` are what tell them apart, and what the three counts and `is_open` still stay off
 * this shape for is TASK-108's recorded reason — they are facts about work an `Interviewer` is not
 * part of, and this is the one route on the vacancy surface they may call.
 */
export type RoleOption = ResponseOf<'/api/v1/roles/options/', 'get'>[number];

export const fetchRoleOptions = () => apiGet('/api/v1/roles/options/');

/**
 * The vacancies **this caller** may filter a candidate list by (backend TASK-108) — the route that
 * makes §6.3's vacancy filter one control instead of two.
 *
 * ⚠️ **All three groups may call it, which the catalogue cannot say.** `GET /api/v1/roles/` answers an
 * `Interviewer` 403, so their options were built from the rows of the current page while an `Admin`'s
 * read the catalogue: one control, two behaviours, and a vacancy absent from the page unreachable
 * without clearing the filter. What differs here is the **queryset** — an `Interviewer` is answered the
 * vacancies their own assignments touch — and never the status code.
 *
 * ⚠️ **No viewer segment in the key, and the reasoning is `queryKeys.roles`'s rather than a weaker copy
 * of it.** This route *is* narrowed per caller, unlike the catalogue — so the key **does** carry the
 * viewer, exactly as the candidate and interview families do. Sharing one entry between an `Admin` and
 * an `Interviewer` under a mounted screen is how one of them would be served the other's list.
 */
export function roleOptionsQuery(viewerId: number) {
  return queryOptions<RoleOption[], ApiError>({
    queryKey: queryKeys.roleOptions(viewerId),
    queryFn: fetchRoleOptions,
  });
}

/**
 * **Which sets a vacancy offers** — `GET /api/v1/roles/{role_id}/question-sets/`, §6.8's control
 * (Sprint 20, ADR 0018 decision 3).
 *
 * ⚠️ **Here rather than in `api/questionSets.ts`, and that is the path and the permission rather than
 * a filing preference.** The address is the vacancy's, the read is the catalogue's read
 * (`read_access` — Admin and Recruiter, an Interviewer 403, exactly as `GET /api/v1/roles/`) and the
 * two writes are the catalogue's write (`write_access` — Admin), so this is one of the routes that
 * really is a vacancy's. What a set *is* lives in `api/questionSets.ts`, and the row type is imported
 * from there rather than restated.
 *
 * ⚠️ **Unpaginated on purpose**, exactly as `GET /api/v1/roles/options/` is: it is one vacancy's
 * offer, bounded by how many sets somebody attached, and *"a page-2 offer is an offer that hides
 * options"*.
 *
 * ⚠️ **A retired set stays attached and is listed, reading `is_active: false`** — retiring is not
 * detaching, and hiding it here would make it invisible to whoever wants to restore it.
 *
 * No viewer in the key, on `queryKeys.roles`' own argument: refused per caller, never narrowed.
 */
export function roleQuestionSetsQuery(roleId: number) {
  return queryOptions<QuestionSet[], ApiError>({
    queryKey: queryKeys.roleQuestionSets(roleId),
    queryFn: () =>
      apiGet('/api/v1/roles/{role_id}/question-sets/', { params: { role_id: roleId } }),
  });
}

/**
 * **Admin only**, the catalogue's own write rule — a vacancy is not an `Interviewer`'s surface at all
 * and a `Recruiter` reads this screen and writes nothing on it (§5).
 *
 * ⚠️ **Idempotent, and it answers with the vacancy's sets after the change.** A many-to-many has no
 * second state to report, so attaching a set that is already attached is not a 409 and not an
 * *"already attached"* — the caller asked for a state and that state is what they get.
 *
 * ⚠️ **Attaching is an offer, never a rule** (ADR 0018 decision 3, which is ADR 0010's reasoning with
 * the nouns changed). It decides what §6.5's scheduling dialog shows **first** and nothing else: no
 * endpoint in this API refuses a set on the grounds that it is not attached to the vacancy.
 */
export const attachQuestionSet = (roleId: number, questionSetId: number) =>
  apiPost(
    '/api/v1/roles/{role_id}/question-sets/',
    { question_set_id: questionSetId },
    { params: { role_id: roleId } },
  );

/**
 * The other half — **Admin only**, equally idempotent (detaching a set that is not attached is a no-op
 * with the same answer), and it changes **no interview that was frozen from that set**:
 * `Interview.question_set` still points at it and every `Score` is untouched (ADR 0004, feedback
 * protection #1).
 */
export const detachQuestionSet = (roleId: number, questionSetId: number) =>
  apiPost('/api/v1/roles/{role_id}/question-sets/{question_set_id}/detach/', undefined, {
    params: { role_id: roleId, question_set_id: questionSetId },
  });
